/**
 * The client's entry point.
 */
import { fromJS } from 'immutable';
import { StyleRoot } from 'radium';
import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { ConnectedRouter } from 'connected-react-router/immutable';
import firebase from 'firebase/app';
import 'firebase/auth';
import 'firebase/database';
import 'firebase/firestore';
import { ReactReduxFirebaseProvider } from 'react-redux-firebase';
import { createFirestoreInstance } from 'redux-firestore';
import * as Sentry from '@sentry/browser';
import { init, setUserData } from '@actions/global';
import { getConfiguration, getUserData } from '@api/config';
import { includes, isEmpty } from 'lodash';
import { getRoutes } from './routes';
import { configureStore } from './store/configureStore';

const ENVIRONMENT = process.env.NODE_ENV;

// Enable some stuff during development to ease debugging
if (ENVIRONMENT !== 'production') {
  // For dev tool support, attach to window...
  window.React = React;
}

//Sentry is for error logging
const sentrySetUp = environment => {
  if (includes(['Production', 'Stage'], environment)) {
    Sentry.init({
      dsn: 'https://71445abc74e043a8b3bc2e7d3ca99136@sentry.io/1502148',
      environment: environment,
    });
  }
};

const intercomScriptString = APP_ID => {
  const srcUrl = JSON.stringify(`https://widget.intercom.io/widget/${APP_ID}`);
  return `(function(){var w=window;var ic=w.Intercom;if(typeof ic==="function"){ic('reattach_activator');ic('update',w.intercomSettings);}else{var d=document;var i=function(){i.c(arguments);};i.q=[];i.c=function(args){i.q.push(args);};w.Intercom=i;var l=function(){var s=d.createElement('script');s.type='text/javascript';s.async=true;s.src=${srcUrl};var x=d.getElementsByTagName('script')[0];x.parentNode.insertBefore(s, x);};if(document.readyState==='complete'){l();}else if(w.attachEvent){w.attachEvent('onload',l);}else{w.addEventListener('load',l,false);}}})();`;
};

const intercomSetUp = (key = '') => {
  if (isEmpty(key)) {
    return;
  }
  var script = document.createElement('script');
  script.innerHTML = intercomScriptString(key);
  document.getElementsByTagName('head')[0].appendChild(script);
};

async function boot() {
  const configuration = await getConfiguration();
  const { env } = configuration;
  sentrySetUp(env);
  intercomSetUp(configuration.keys.intercom);
  const { history, store } = configureStore(fromJS({}));
  await store.dispatch(init(configuration));
  // firebase / firestore configuration
  firebase.initializeApp(configuration.keys.firebaseConfig);
  const userData = await getUserData(firebase);
  await store.dispatch(setUserData(userData));
  const rrfProps = {
    firebase,
    config: {
      userProfile: 'users',
      enableLogging: false,
    },
    dispatch: store.dispatch,
    createFirestoreInstance,
  };

  ReactDOM.render(
    <StyleRoot>
      <Provider store={store}>
        <ReactReduxFirebaseProvider {...rrfProps}>
          <ConnectedRouter history={history}>
            {getRoutes(store)}
          </ConnectedRouter>
        </ReactReduxFirebaseProvider>
      </Provider>
    </StyleRoot>,
    document.getElementById('root'),
  );
}

boot();
